import { defineStore, acceptHMRUpdate } from "pinia";
import { ref } from "vue";
import { usePlugins } from "@/stores/helpers/usePlugins";
import { updateInArray, removeFromArray } from "@/stores/helpers/helpers";
import client from "@/api/openapi-ts";
import type { components } from "@/api/schema.d.ts";
import {
  useAppClientStore,
  type AppClientEditable,
} from "@/stores/AppClientStore";

type schemas = components["schemas"];

function ensureAppClientPaidEarnedMediaProjects(
  appClient: AppClientEditable | undefined,
): schemas["AppClientPaidEarnedMediaProject"][] | undefined {
  if (!appClient) return undefined;
  appClient.paid_earned_media_projects ??= [];
  return appClient.paid_earned_media_projects;
}

type Loading = {
  dates: boolean;
  races: boolean;
  sponsors: boolean;
  sponsorsForCreativesTab: boolean;
  projectRaces: boolean;
  projectSponsors: boolean;
  projects: boolean;
  topline_numbers: {
    table: boolean;
    charts: boolean;
  };
  creatives_at_a_glance: boolean;
  date_tracker: boolean;
};

type TaskIds = {
  dates?: string;
  races?: string;
  sponsors?: string;
  sponsorsForCreativesTab?: string;
  toplineNumbersData?: string;
  creativesAtAGlanceData?: string;
  dateTrackerData?: string;
  projectRaces?: string;
  projectSponsors?: string;
};

type Errors = {
  toplineNumbersData: string | undefined;
  creativesAtAGlanceData: string | undefined;
  dateTrackerData: string | undefined;
};

type Geography = {
  name: string;
  ref_id: number;
  geography_type: string;
};

type Filters = {
  project?: Project;
  start_date: string | null;
  end_date: string | null;
  meta_audience_id?: number;
  race_type?: string | null;
  races?: string[];
  geographies: Geography[];
  medium_types?: string[];
  sponsor_ids?: number[];
  metric: string | null;
  color_by?: string;
  affiliation?: string;
};

type CreativesSideFilters = {
  meta_audience_ids?: number[];
  geographies: Geography[];
  sponsor_ids?: number[];
};

type ProjectMetaData = {
  base_project_id?: number;
  start_date?: string;
  end_date?: string;
  geographies: object[];
  meta_audience_ids: number[];
  race_types: string[];
  races: string[];
  sponsor_ids: number[];
  sponsors: string[];
};

type Project = {
  id?: number;
  name: string;
  description?: string;
  meta_data?: ProjectMetaData;
  shared_with?: string[];
};

const defaultLoadingState = () => {
  return {
    dates: false,
    races: false,
    sponsors: false,
    sponsorsForCreativesTab: false,
    projectRaces: false,
    projectSponsors: false,
    projects: false,
    topline_numbers: {
      table: false,
      charts: false,
    },
    creatives_at_a_glance: false,
    date_tracker: false,
  };
};

const defaultFilterState = () => {
  return {
    project: undefined,
    start_date: null,
    end_date: null,
    meta_audience_id: undefined,
    race_type: null,
    races: [],
    geographies: [],
    sponsor_ids: [],
    medium_types: [],
    metric: null,
    color_by: undefined,
    affiliation: undefined,
  };
};

const defaultCreativesSideFilterState = () => {
  return {
    meta_audience_ids: [],
    geographies: [],
    sponsor_ids: [],
  };
};

export const usePaidEarnedMediaStore = defineStore("paidEarnedMedia", () => {
  const appClientStore = useAppClientStore();
  const loading = ref<Loading>(defaultLoadingState());
  const savingProject = ref(false);
  const taskIds = ref<TaskIds>({
    dates: undefined,
    races: undefined,
    sponsors: undefined,
    sponsorsForCreativesTab: undefined,
    toplineNumbersData: undefined,
    creativesAtAGlanceData: undefined,
    dateTrackerData: undefined,
    projectRaces: undefined,
    projectSponsors: undefined,
  });
  const errors = ref<Errors>({
    toplineNumbersData: undefined,
    creativesAtAGlanceData: undefined,
    dateTrackerData: undefined,
  });
  const userProjects = ref<schemas["PaidEarnedMediaProject"][]>([]);
  const orgProjects = ref<schemas["PaidEarnedMediaProject"][]>([]);
  const dates = ref<schemas["PaidEarnedMediaDates"] | undefined>(undefined);
  const races = ref<schemas["PaidEarnedMediaRace"][]>([]);
  const sponsors = ref<schemas["PaidEarnedMediaSponsor"][]>([]);
  const sponsorsForCreativesTab = ref<schemas["PaidEarnedMediaSponsor"][]>([]);
  const projectRaces = ref<schemas["PaidEarnedMediaRace"][]>([]);
  const projectSponsors = ref<schemas["PaidEarnedMediaSponsor"][]>([]);
  const filters = ref<Filters>(defaultFilterState());
  const applied = ref(false);
  const appliedFilters = ref<Filters>(defaultFilterState());
  const creativesSideFilters = ref<CreativesSideFilters>(
    defaultCreativesSideFilterState(),
  );
  const appliedCreativesSideFilters = ref<CreativesSideFilters>(
    defaultCreativesSideFilterState(),
  );
  const toplineNumbers = ref<
    schemas["PaidEarnedMediaGetToplineNumbersResponse"]
  >({});
  const creativesAtAGlance = ref<
    schemas["PaidEarnedMediaGetCreativesAtAGlanceResponse"][]
  >([]);
  const dateTracker = ref<schemas["PaidEarnedMediaGetDateTrackerResponse"]>({});

  const { $ws } = usePlugins();

  return {
    taskIds,
    loading,
    errors,
    savingProject,
    userProjects,
    orgProjects,
    dates,
    races,
    sponsors,
    sponsorsForCreativesTab,
    projectRaces,
    projectSponsors,
    filters,
    applied,
    appliedFilters,
    creativesSideFilters,
    appliedCreativesSideFilters,
    toplineNumbers,
    creativesAtAGlance,
    dateTracker,

    resetFilters() {
      loading.value = defaultLoadingState();
      appliedFilters.value = defaultFilterState();
      appliedCreativesSideFilters.value = defaultCreativesSideFilterState();
      filters.value = defaultFilterState();
      creativesSideFilters.value = defaultCreativesSideFilterState();
    },
    isLoading(prop: string) {
      if (prop === "topline_numbers") {
        return loading.value[prop].table || loading.value[prop].charts;
      } else {
        return loading.value[prop as keyof Loading];
      }
    },
    async getDateRange() {
      loading.value.dates = true;
      try {
        const { data } = await client.GET("/api/v1/paid_earned_media/dates");
        taskIds.value.dates = data?.task_id as string;
        if ($ws) {
          $ws.subscribe({
            channel: "PaidEarnedMedia",
            payload: { id: data?.task_id },
          });
        }
      } catch (error) {
        console.error(error);
        loading.value.dates = false;
      }
    },
    async getRaces() {
      if (filters.value.start_date && filters.value.end_date) {
        loading.value.races = true;
        try {
          const { data } = await client.POST(
            "/api/v1/paid_earned_media/races",
            {
              body: {
                start_date: filters.value.start_date,
                end_date: filters.value.end_date,
              },
            },
          );
          taskIds.value.races = data?.task_id as string;
          if ($ws) {
            $ws.subscribe({
              channel: "PaidEarnedMedia",
              payload: { id: data?.task_id },
            });
          }
        } catch (error) {
          console.error(error);
          loading.value.races = false;
        }
      }
    },
    async getProjectRaces(startDate: string, endDate: string) {
      loading.value.projectRaces = true;
      try {
        const { data } = await client.POST("/api/v1/paid_earned_media/races", {
          body: {
            start_date: startDate,
            end_date: endDate,
          },
        });
        taskIds.value.projectRaces = data?.task_id as string;
        if ($ws) {
          $ws.subscribe({
            channel: "PaidEarnedMedia",
            payload: { id: data?.task_id },
          });
        }
      } catch (error) {
        console.error(error);
        loading.value.projectRaces = false;
      }
    },
    async getSponsors(projectRaces: string[], creativesTab: boolean = false) {
      const sponsorType = creativesTab ? "sponsorsForCreativesTab" : "sponsors";
      loading.value[sponsorType] = true;
      const loadCreativesTab =
        !creativesTab && !creativesSideFilters.value.sponsor_ids?.length;
      if (loadCreativesTab) {
        loading.value.sponsorsForCreativesTab = true;
      }
      const filterType = creativesTab
        ? creativesSideFilters.value
        : filters.value;

      try {
        const { data } = await client.POST(
          "/api/v1/paid_earned_media/sponsors",
          {
            body: {
              start_date: filters.value.start_date,
              end_date: filters.value.end_date,
              geographies: filterType.geographies,
              races: projectRaces || filters.value.races,
            },
          },
        );
        taskIds.value[sponsorType] = data?.task_id as string;
        if (sponsorType !== "sponsorsForCreativesTab" && loadCreativesTab) {
          taskIds.value.sponsorsForCreativesTab = data?.task_id as string;
        }
        if ($ws) {
          $ws.subscribe({
            channel: "PaidEarnedMedia",
            payload: { id: data?.task_id },
          });
        }
      } catch (error) {
        console.error(error);
        loading.value[sponsorType] = false;
        if (loading.value.sponsorsForCreativesTab) {
          loading.value.sponsorsForCreativesTab = false;
        }
      }
    },
    async getProjectSponsors(
      dateRange: string[],
      geographies: Geography[],
      races: string[],
    ) {
      loading.value.projectSponsors = true;

      try {
        const { data } = await client.POST(
          "/api/v1/paid_earned_media/sponsors",
          {
            body: {
              start_date: dateRange[0],
              end_date: dateRange[1],
              geographies: geographies,
              races: races,
            },
          },
        );
        taskIds.value.projectSponsors = data?.task_id as string;
        if ($ws) {
          $ws.subscribe({
            channel: "PaidEarnedMedia",
            payload: { id: data?.task_id },
          });
        }
      } catch (error) {
        console.error(error);
        loading.value.projectSponsors = false;
      }
    },
    async getToplineNumbers(toplineFilters: any, chartsOnly: boolean) {
      errors.value.toplineNumbersData = undefined;
      if (!chartsOnly) {
        loading.value.topline_numbers.table = true;
      }
      loading.value.topline_numbers.charts = true;

      const {
        affiliation: _affiliation,
        project: _project,
        race_type: _race_type,
        ...filtersBody
      } = filters.value;

      try {
        const { data } = await client.POST(
          "/api/v1/paid_earned_media/topline_numbers",
          {
            body: {
              ...filtersBody,
              ...toplineFilters,
            },
          },
        );

        taskIds.value.toplineNumbersData = data?.task_id as string;
        if ($ws) {
          $ws.subscribe({
            channel: "PaidEarnedMedia",
            payload: { id: data?.task_id },
          });
        }
      } catch (error) {
        console.error(error);
        loading.value.topline_numbers.table = false;
        loading.value.topline_numbers.charts = false;
      }
    },
    async getCreativesAtAGlance(creativesFilters: any) {
      errors.value.creativesAtAGlanceData = undefined;
      loading.value.creatives_at_a_glance = true;

      const {
        affiliation: _affiliation,
        color_by: _color_by,
        meta_audience_id: _meta_audience_id,
        metric: _metric,
        project: _project,
        race_type: _race_type,
        ...filtersBody
      } = filters.value;

      try {
        const { data } = await client.POST(
          "/api/v1/paid_earned_media/creatives_at_a_glance",
          {
            body: {
              ...filtersBody,
              ...creativesSideFilters.value,
              ...creativesFilters,
            },
          },
        );
        taskIds.value.creativesAtAGlanceData = data?.task_id as string;
        if ($ws) {
          $ws.subscribe({
            channel: "PaidEarnedMedia",
            payload: { id: data?.task_id },
          });
        }
      } catch (error) {
        console.error(error);
        loading.value.creatives_at_a_glance = false;
      }
    },
    async getDateTracker(dateTrackerFilters: any) {
      errors.value.dateTrackerData = undefined;
      loading.value.date_tracker = true;

      const {
        affiliation: _affiliation,
        color_by: _color_by,
        project: _project,
        race_type: _race_type,
        ...filtersBody
      } = filters.value;

      try {
        const { data } = await client.POST(
          "/api/v1/paid_earned_media/date_tracker",
          {
            body: {
              ...filtersBody,
              ...dateTrackerFilters,
            },
          },
        );
        taskIds.value.dateTrackerData = data?.task_id as string;
        if ($ws) {
          $ws.subscribe({
            channel: "PaidEarnedMedia",
            payload: { id: data?.task_id },
          });
        }
      } catch (error) {
        console.error(error);
        loading.value.date_tracker = false;
      }
    },
    async getProjects() {
      loading.value.projects = true;

      try {
        const { data } = await client.GET("/api/v1/paid_earned_media/projects");
        userProjects.value = data as schemas["PaidEarnedMediaProject"][];
      } catch (error) {
        console.error(error);
      } finally {
        loading.value.projects = false;
      }
    },
    async createProject(
      project: schemas["PaidEarnedMediaProjectCreateProjectRequest"],
    ) {
      savingProject.value = true;

      try {
        const { data } = await client.POST(
          "/api/v1/paid_earned_media/projects",
          {
            body: project,
          },
        );
        userProjects.value.push(data as schemas["PaidEarnedMediaProject"]);
      } catch (error) {
        console.error(error);
      } finally {
        savingProject.value = false;
      }
    },
    async updateProject(projectId: number, project: any) {
      savingProject.value = true;

      const { client_id: _client_id, ...projectBody } = project;

      try {
        const { data } = await client.PUT(
          "/api/v1/paid_earned_media/projects/{project_id}",
          {
            params: { path: { project_id: projectId } },
            body: projectBody as schemas["PaidEarnedMediaProjectUpdateProjectBodyRequest"],
          },
        );
        updateInArray(
          userProjects.value,
          data as schemas["PaidEarnedMediaProject"],
        );
      } catch (error) {
        console.error(error);
      } finally {
        savingProject.value = false;
      }
    },
    async deleteProject(projectId: number) {
      savingProject.value = true;

      try {
        await client.DELETE("/api/v1/paid_earned_media/projects/{project_id}", {
          params: { path: { project_id: projectId } },
        });
        removeFromArray(userProjects.value, projectId);
        const idx = userProjects.value.findIndex((p) => p.id === projectId);
        if (idx >= 0) {
          userProjects.value.splice(idx, 1);
        }
      } catch (error) {
        console.error(error);
      } finally {
        savingProject.value = false;
      }
    },
    async getOrgProjects(orgId: number) {
      loading.value.projects = true;
      try {
        const { data } = await client.GET(
          "/api/v1/paid_earned_media/projects/for_org/{org_id}",
          {
            params: { path: { org_id: orgId } },
          },
        );
        orgProjects.value = data as schemas["PaidEarnedMediaProject"][];
      } catch (error) {
        console.error(error);
      } finally {
        loading.value.projects = false;
      }
    },
    async createOrgProject(project: any, orgId: number) {
      savingProject.value = true;

      const { shared_with: _shared_with, ...projectBody } = project;

      try {
        const { data } = await client.POST(
          "/api/v1/paid_earned_media/projects/for_org/{org_id}",
          {
            params: { path: { org_id: orgId } },
            body: projectBody as schemas["PaidEarnedMediaProjectCreateProjectForOrgBodyRequest"],
          },
        );
        orgProjects.value.push(data as schemas["PaidEarnedMediaProject"]);
      } catch (error) {
        console.error(error);
      } finally {
        savingProject.value = false;
      }
    },
    async updateOrgProject(project: any, projectId: number, orgId: number) {
      savingProject.value = true;

      const { shared_with: _shared_with, ...projectBody } = project;

      try {
        const { data } = await client.PUT(
          "/api/v1/paid_earned_media/projects/{project_id}/for_org/{org_id}",
          {
            params: { path: { project_id: projectId, org_id: orgId } },
            body: projectBody as schemas["PaidEarnedMediaProjectUpdateProjectForOrgBodyRequest"],
          },
        );
        updateInArray(
          orgProjects.value,
          data as schemas["PaidEarnedMediaProject"],
        );
      } catch (error) {
        console.error(error);
      } finally {
        savingProject.value = false;
      }
    },
    async deleteOrgProject(projectId: number, orgId: number) {
      savingProject.value = true;

      try {
        await client.DELETE(
          "/api/v1/paid_earned_media/projects/{project_id}/for_org/{org_id}",
          {
            params: { path: { project_id: projectId, org_id: orgId } },
          },
        );
        removeFromArray(orgProjects.value, projectId);
        const idx = orgProjects.value.findIndex((p) => p.id === projectId);
        if (idx >= 0) {
          orgProjects.value.splice(idx, 1);
        }
      } catch (error) {
        console.error(error);
      } finally {
        savingProject.value = false;
      }
    },
    async createAppClientProject(project: any, appClientId: number) {
      savingProject.value = true;
      const { shared_with: _shared_with, ...projectBody } = project;
      try {
        const { data, response } = await client.POST(
          "/api/v1/paid_earned_media/projects/for_app_client/{app_client_id}",
          {
            params: { path: { app_client_id: appClientId } },
            body: projectBody as schemas["PaidEarnedMediaProjectCreateProjectForAppClientBodyRequest"],
          },
        );
        if (!response.ok) {
          throw new Error(response.statusText);
        }
        ensureAppClientPaidEarnedMediaProjects(appClientStore.appClient)?.push(
          data as schemas["AppClientPaidEarnedMediaProject"],
        );
      } catch (error) {
        console.error(error);
        throw error;
      } finally {
        savingProject.value = false;
      }
    },
    async updateAppClientProject(
      project: any,
      projectId: number,
      appClientId: number,
    ) {
      savingProject.value = true;

      const {
        shared_with: _shared_with,
        client_id: _client_id,
        ...projectBody
      } = project;

      try {
        const { data, response } = await client.PUT(
          "/api/v1/paid_earned_media/projects/{project_id}/for_app_client/{app_client_id}",
          {
            params: {
              path: { project_id: projectId, app_client_id: appClientId },
            },
            body: projectBody as schemas["PaidEarnedMediaProjectUpdateProjectForAppClientBodyRequest"],
          },
        );
        if (!response.ok) {
          throw new Error(response.statusText);
        }
        const projects = ensureAppClientPaidEarnedMediaProjects(
          appClientStore.appClient,
        );
        if (projects) {
          updateInArray(
            projects,
            data as schemas["AppClientPaidEarnedMediaProject"],
          );
        }
      } catch (error) {
        console.error(error);
        throw error;
      } finally {
        savingProject.value = false;
      }
    },
    async deleteAppClientProject(projectId: number, appClientId: number) {
      savingProject.value = true;
      try {
        const { response } = await client.DELETE(
          "/api/v1/paid_earned_media/projects/{project_id}/for_app_client/{app_client_id}",
          {
            params: {
              path: { project_id: projectId, app_client_id: appClientId },
            },
          },
        );
        if (!response.ok) {
          throw new Error(response.statusText);
        }
        const projects = ensureAppClientPaidEarnedMediaProjects(
          appClientStore.appClient,
        );
        if (projects) {
          removeFromArray(projects, projectId);
        }
      } catch (error) {
        console.error(error);
        throw error;
      } finally {
        savingProject.value = false;
      }
    },
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(usePaidEarnedMediaStore, import.meta.hot),
  );
}
